import {Component, OnDestroy, OnInit} from '@angular/core';
import {AclGroup, AclPermission, AclPermissionDescription} from '../../../../models/acl/acl-group';
import {DialogService} from 'primeng/dynamicdialog';
import {AclGroupFormComponent} from '../acl-group-form/acl-group-form.component';
import {ConfirmationService, FilterService, LazyLoadEvent, SelectItem} from 'primeng/api';
import {SessionStorageService} from '../../../../services/session-storage.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {StringifierService} from '../../../../services/stringifier.service';
import {TabPanel, TabView} from 'primeng/tabview';
import {Card} from 'primeng/card';
import {Button, ButtonDirective} from 'primeng/button';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {Tooltip} from 'primeng/tooltip';
import {RestApiService} from '../../../../services/api/rest-api.service';
import {FilterAclGroupsRequest} from '../../../../models/requests/acl/filter-acl-groups-request';
import {getFilterRequest} from '../../../../services/filters/filter-utils';
import {PascalCasePipe} from '../../../../pipes/pascal-case.pipe';
import {NgForOf, NgIf} from '@angular/common';
import {StringFilterComponent} from '../../../../components/table/filters/string/string-filter.component';
import {User} from '../../../../models/user';

@UntilDestroy({checkProperties: true})
@Component({
  standalone: true,
  selector: 'app-acl-groups-list',
  templateUrl: './acl-groups-list.component.html',
  imports: [
    TabView,
    TabPanel,
    Card,
    Button,
    TableModule,
    Tooltip,
    ButtonDirective,
    PascalCasePipe,
    NgForOf,
    StringFilterComponent,
    NgIf
  ],
  styleUrls: ['./acl-groups-list.component.scss']
})
export class AclGroupsListComponent implements OnInit, OnDestroy {

  loading = false;
  groups: AclGroup[] = [];
  permissions: AclPermissionDescription[] = [];
  requestFilter: FilterAclGroupsRequest = {} as FilterAclGroupsRequest;
  filterStringifier: FilterAclGroupsRequest;
  AclPermission = AclPermission;
  token = '';
  botToken = '';
  users: { [userId: number]: User } = {};

  constructor(private api: RestApiService,
              private confirmService: ConfirmationService,
              private dialogService: DialogService,
              private filterService: FilterService,
              private sessionService: SessionStorageService,
              private stringifierService: StringifierService) {
    this.filterStringifier = this.stringifierService.getStringifier(FilterAclGroupsRequest);
  }

  ngOnInit() {
    this.token = this.sessionService.getToken();
    this.loadData(); // senza await
  }

  async loadData() {
    try {
      await this.preloadData();
      const responseBotToken = await this.api.authentication.getBotToken();
      this.botToken = responseBotToken.data;
    } catch {
      // permessi insufficenti
      this.botToken = '';
    }
  }

  ngOnDestroy() {
  }

  // =============================================
  //  METHODS
  // =============================================

  obj(item: AclGroup): AclGroup {
    return item;
  }

  private async preloadData() {
    const response = await this.api.acl.getPermissions();
    if (response?.data) {
      this.permissions = response.data;
    }

    const usersResponse = await this.api.users.getAllUsers();
    this.users = usersResponse.data.reduce((acc, user) => {
      // @ts-ignore
      acc[user.userId] = user;
      return acc;
    }, {});
  }

  add() {
    this.openEditForm(null);
  }

  edit(item: AclGroup) {
    this.openEditForm(item.aclGroupId);
  }

  private openEditForm(groupId: number | null) {
    AclGroupFormComponent.openDialog(this.dialogService, groupId)
      .onClose
      .pipe(untilDestroyed(this))
      .subscribe(async group => {
        await this.reloadTable();
      });
  }

  delete(item: AclGroup) {
    this.confirmService.confirm({
      message: `Sei sicuro di voler eliminare la ACL "${item.name}"?`,
      header: 'Eliminazione ACL',
      accept: async () => {
        await this.api.acl.deleteById(item.aclGroupId);
        await this.reloadTable();
      },
      reject: () => {
      }
    });
  }

  async filter(event: TableLazyLoadEvent) {
    this.requestFilter = getFilterRequest<FilterAclGroupsRequest>(event);
    setTimeout(() => {
      // Ricarica la tabella in un nuovo thread. Evita aggiornamenti delle props durante la fase di render
      this.reloadTable();
    }, 0);
  }

  private async reloadTable() {
    try {
      this.loading = true;
      const response = await this.api.acl.getGroups(this.requestFilter);
      this.groups = response.data.dataList;
    } finally {
      this.loading = false;
    }
  }

  copyToClipboard(copyText: string) {
    navigator.clipboard.writeText(copyText);
  }

  getPermissionCategory(permission: AclPermission) {
    const descriptor = this.permissions.find(x => x.id === permission);
    return descriptor?.category ?? '??';
  }

  getPermissionDescription(permission: AclPermission) {
    const descriptor = this.permissions.find(x => x.id === permission);
    return descriptor?.description ?? '??';
  }

  protected throwException() {
    throw new Error("Sentry Test Error");
  }
}
